/* eslint-disable no-console */
import { Wrap, StyledButton as Button } from './styles';

import GbaProject from 'services/models/GbaProject';
import geoAlert from 'services/utils/customAlert';

import modalImport from './components/modalImport.js';

import AppContext from '../../../../../../../services/context';

import Icon from '@map/components/Icons';
import LinearProgress from '@material-ui/core/LinearProgress';
import FileInput from 'components/FileInput';
/**
 * Tooltip é uma ferramenta utilizada para especificar informações extras
 * sobre algo quando o usuário move o ponteiro do mouse sobre um elemento.
 */
import Tooltip from 'components/Tooltip';
/**
 * useEffect é um hook que permite executar efeitos colaterais em
 * seus componentes, por exemplo: buscar dados, atualizar diretamente
 * o DOM e cronômetros, ele aceita dois argumentos o segundo é opcional.
 * 
 * useState é um hook que nos permite rastrear o estado em um
 * componente de função, o estado geralmente se refere a dados ou
 * propriedads que precisam ser rastreador em um aplicativo.
 * 
 * useRef permite que persista valores entre renderizações, ele pode
 * ser usado para armazenar um valor mutável que não causa uma nova
 * renderização quando atualizado e pode ser usado para acessar di-
 * retamente um elemento do DOM.
 */
import { useEffect, useState, useRef, useContext } from 'react';
/**
 *  useModules é responsavel por gerenciar os modulos do GeoBahia,
 *  todos os módulos criados na ADM.
 * 
 *  useMap é um hook personalizado criado para gerenciar a comunicação
 *  dos componentes com o mapa.
 */
import { useModule, useMap } from 'views/MapView/hooks';

/**
 * Função responsável por abertura do modal para importar arquivos 
 * 
 * @param {string} mapContext Context
 * @param {boolean} onClose Fechar o modal
 * @returns Retorna o carregamento do projeto importado e fecha
 * o modal depois.
 */
export const ImportView = ({ mapContext, onClose }) => {
  const iframeRef = useRef();

  const appContext = useContext(AppContext);
  const { setBottomPanelIsOpen } = appContext;
  
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gbaProject, setGbaProject] = useState(null);
  const [layers, setLayers] = useState(null);
  const [error, setError] = useState(null);
  const moduleCtx = useModule();
  const mapCtx = useMap();
  const {setLoadingInMap} = useMap();

  // Replay da animação SVG
  useEffect(() => {
    let animationInterval;
    if (iframeRef.current && loading) {
      iframeRef.current.contentWindow.postMessage('play', '*');
      animationInterval = setInterval(
        () => iframeRef.current.contentWindow.postMessage('play', '*'),
        4000
      );
    }
    return () => {
      if (animationInterval) clearInterval(animationInterval);
    };
  }, [iframeRef, loading]);

  useEffect(() => {
    if (moduleCtx.activeModule && file && !gbaProject) {
      let newGbaProject = new GbaProject({ ...mapCtx, ...moduleCtx }, file);
      setLoading(true);
      newGbaProject
        .importFromFile()
        .then(() => {
          if(newGbaProject.moduleId !== moduleCtx.activeModule.id){
            onClose && onClose();
            geoAlert({
              title: 'Erro ao importar projeto!',
              message: `A importação deste projeto requer o uso do módulo ${newGbaProject.moduleTitle}.`,
            });
          }           
          setGbaProject(newGbaProject);
          setLayers(newGbaProject.layers);
        })
        .catch((e) => console.error('Error importing GBA:', e))
        .finally(() => setLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapCtx, moduleCtx, file, gbaProject]);

  return (
    <Wrap>
      {!file ? (
        <div className="file-input">
          <FileInput
            accept=".gba"
            onChange={(newFile) => setFile(newFile)}
            extensionCheckMsg="Não foi possível inserir o arquivo. Arquivo com formato não permitido."
          />
        </div>
      ) : (
        <div className="progress-area">
          <div className="progress-head">
            <h4>Importando</h4>
            {!loading && <h4>100%</h4>}
          </div>
          <LinearProgress
            variant={loading ? 'indeterminate' : 'determinate'}
            value="100"
          />
        </div>
      )}
      <div className={`animated-svg ${loading && 'show-animation'}`}>
        <iframe
          ref={iframeRef}
          title="svg-animation"
          frameBorder="0"          
        ></iframe>
      </div>
      {!loading && gbaProject && (
        <>
          <div className="gba-data">
            {layers &&
              layers.map((l, i) => (
                <Tooltip
                  title={
                    l.error
                      ? 'Camada removida do Módulo.'
                      : 'Pronta para ser importada.'
                  }
                  placement="top-end"
                  key={i}
                >
                  <div className="layer">
                    <div className="title">
                      <p>{l.title || l.getTitle()}</p>
                      <span>{l.length && `(${l.length})`}</span>
                    </div>
                    <div className={`stat-icon ${l.error && 'error'}`}>
                      {l.error ? (
                        <Icon i="map-cross" />
                      ) : (
                        <Icon i="map-check" />
                      )}
                    </div>
                    <div
                      className={`fake-progress ${l.error && 'error'}`}
                    ></div>
                    <button
                      className="remove-button"
                      onClick={() => {
                        gbaProject.removeLayer(i);
                        setGbaProject(gbaProject);
                        setLayers(gbaProject.layers);
                      }}
                    >
                      <Icon i="map-trash" width="10" height="11" />
                    </button>
                  </div>
                </Tooltip>
              ))}
          </div>
          <span className="error-message">{error}</span>
        </>
      )}
      <div className="footer">
        <div className="button-area">
          {loading ? (
            <Button
            variant="contained"
            disabled
            onClick={() => {
              setLoading(true);
              let gbaError = gbaProject.validate();
              if (gbaError === true) {
                gbaProject.applyImportOnMap().then(() => {
                  setFile(null);
                  setLoading(false);
                  onClose && onClose();
                });
              } else {
                setLoading(false);
                setError(gbaError);
              }
            }}
          >
            Importar
          </Button>
          ) : (
            <Button
            variant="contained"
            disabled={!gbaProject}
            onClick={() => {              
              modalImport({
                message:
                  'Ao importar o projeto, o sistema irá substituir as camadas da lista de camadas, deseja continuar?',
                callback: (confirmed) => {
                  if (confirmed) {
                    setLoading(true);
                    // Fecha tabela de atributos
                    setBottomPanelIsOpen(false)
                    let gbaError = gbaProject.validate();
                    if (gbaError === true) {             
                      gbaProject.applyImportOnMap(confirmed)
                      .then(() => {
                        setFile(null);
                        onClose && onClose();
                        setLoading(false);
                      })
                    } else {
                      setLoadingInMap(false);
                      setError(gbaError);
                    }      
                  }
                },
              });
            }}
          >
            Importar
          </Button>
          )}
        </div>
      </div>
    </Wrap>
  );
};

export default ImportView;
